import { useEffect, useMemo, useRef, useState } from "react";
import { X, ExternalLink, ArrowRight } from "lucide-react";
import { api } from "../lib/api";
import { useAuth } from "../context/AuthContext";

// ---------------------------------------------------------------------------
// Session-scoped rotation + dismissal memory (sessionStorage; safe fallback)
// ---------------------------------------------------------------------------
const SESSION_KEY = "pmposhan_ad_session";
const DISMISS_KEY = "pmposhan_ad_dismissed";
const ROTATION_KEY = "pmposhan_ad_rotation";
const MODAL_SHOWN_KEY = "pmposhan_ad_modal_shown";

function safeStorage() {
  try { return window.sessionStorage; } catch { return null; }
}
function sessionId() {
  const s = safeStorage();
  if (!s) return "anon";
  let sid = s.getItem(SESSION_KEY);
  if (!sid) { sid = Math.random().toString(36).slice(2, 12); s.setItem(SESSION_KEY, sid); }
  return sid;
}
function isDismissed(campaignId, placement) {
  const s = safeStorage(); if (!s) return false;
  try {
    const raw = JSON.parse(s.getItem(DISMISS_KEY) || "{}");
    return !!raw[`${campaignId}:${placement}`];
  } catch { return false; }
}
function markDismissed(campaignId, placement) {
  const s = safeStorage(); if (!s) return;
  try {
    const raw = JSON.parse(s.getItem(DISMISS_KEY) || "{}");
    raw[`${campaignId}:${placement}`] = Date.now();
    s.setItem(DISMISS_KEY, JSON.stringify(raw));
  } catch {}
}
// Weighted rotation with per-session pointer, so consecutive placements
// on the same page don't all show the same ad.
function pickCampaign(items, placement) {
  if (!items || items.length === 0) return null;
  const eligible = items.filter((a) => !isDismissed(a.campaign_id, placement));
  if (eligible.length === 0) return null;
  const s = safeStorage();
  let rot = 0;
  if (s) {
    try {
      const raw = JSON.parse(s.getItem(ROTATION_KEY) || "{}");
      rot = raw[placement] || 0;
      raw[placement] = (rot + 1) % 10000;
      s.setItem(ROTATION_KEY, JSON.stringify(raw));
    } catch {}
  }
  // Weighted expansion — repeat each campaign by its weight to bias fairly.
  const bag = [];
  for (const a of eligible) {
    const w = Math.max(1, Math.min(100, a.weight || 10));
    for (let i = 0; i < Math.max(1, Math.floor(w / 10)); i++) bag.push(a);
  }
  return bag[rot % bag.length] || eligible[0];
}

// Accent colour presets — keep the ad card visually distinct per campaign
// but consistent with the app's design system.
const ACCENT = {
  emerald: { border: "border-emerald-200", bg: "from-emerald-50 to-white", chip: "bg-emerald-100 text-emerald-800 border-emerald-200", btn: "bg-emerald-700 hover:bg-emerald-800" },
  blue:    { border: "border-blue-200",    bg: "from-blue-50 to-white",    chip: "bg-blue-100 text-blue-800 border-blue-200",       btn: "bg-blue-700 hover:bg-blue-800" },
  orange:  { border: "border-orange-200",  bg: "from-orange-50 to-white",  chip: "bg-orange-100 text-orange-800 border-orange-200", btn: "bg-orange-600 hover:bg-orange-700" },
};

// ---------------------------------------------------------------------------
// <PromotionalAd placement="..." variant="card|horizontal|compact|modal" />
// ---------------------------------------------------------------------------
export default function PromotionalAd({ placement = "homepage", variant = "card", onDismiss, forceCampaignId }) {
  const { user } = useAuth();
  const [items, setItems] = useState(null);
  const [visible, setVisible] = useState(true);
  const [tracked, setTracked] = useState(false);
  const cardRef = useRef(null);

  useEffect(() => {
    let cancelled = false;
    api.adsActive(placement).then((r) => { if (!cancelled) setItems(r.items || []); }).catch(() => setItems([]));
    return () => { cancelled = true; };
  }, [placement]);

  const ad = useMemo(() => {
    if (!items || items.length === 0) return null;
    if (forceCampaignId) return items.find((a) => a.campaign_id === forceCampaignId) || null;
    return pickCampaign(items, placement);
  }, [items, placement, forceCampaignId]);

  // Hide for premium users unless the campaign has show_to_premium: true
  const premium = !!user?.subscription?.status && user?.subscription?.status === "active" && new Date(user?.subscription?.expiry_date || 0) > new Date();
  const hideForPremium = premium && !ad?.show_to_premium && variant !== "compact";

  // Impression tracking — fire once when the card scrolls into view.
  useEffect(() => {
    if (!ad || tracked || !cardRef.current) return;
    if (typeof IntersectionObserver === "undefined") {
      api.adImpression(ad.campaign_id, placement, sessionId());
      setTracked(true);
      return;
    }
    const obs = new IntersectionObserver((entries) => {
      for (const e of entries) {
        if (e.isIntersecting && !tracked) {
          api.adImpression(ad.campaign_id, placement, sessionId());
          setTracked(true);
          obs.disconnect();
          break;
        }
      }
    }, { threshold: 0.5 });
    obs.observe(cardRef.current);
    return () => obs.disconnect();
  }, [ad, placement, tracked]);

  const onClick = () => {
    if (!ad) return;
    api.adClick(ad.campaign_id, placement, sessionId());
  };
  const dismiss = () => {
    if (ad) markDismissed(ad.campaign_id, placement);
    setVisible(false);
    onDismiss && onDismiss();
  };

  if (!visible || !ad || hideForPremium) return null;

  const a = ACCENT[ad.accent] || ACCENT.emerald;

  // -------------------------------------------------------------------------
  // Variant: card (default) — vertical native card for homepage/dashboard.
  // -------------------------------------------------------------------------
  if (variant === "card") {
    return (
      <aside ref={cardRef}
             role="complementary"
             aria-label={`${ad.label}: ${ad.name}`}
             className={`relative rounded-2xl border ${a.border} bg-gradient-to-br ${a.bg} p-3 sm:p-4 shadow-sm hover:shadow-md transition-shadow`}
             data-testid={`promo-ad-${ad.campaign_id}`}>
        <div className="flex items-center justify-between gap-2">
          <span className={`inline-flex items-center gap-1 rounded-full border ${a.chip} text-[10px] font-bold uppercase tracking-widest px-2 py-0.5`} data-testid={`promo-ad-label-${ad.campaign_id}`}>{ad.label}</span>
          <button onClick={dismiss} aria-label="Dismiss advertisement" data-testid={`promo-ad-close-${ad.campaign_id}`}
                  className="h-7 w-7 rounded-full hover:bg-slate-100 grid place-items-center text-slate-500">
            <X size={14}/>
          </button>
        </div>
        {ad.image_url && (
          <a href={ad.url} target="_blank" rel="noopener noreferrer" onClick={onClick} className="block mt-2 overflow-hidden rounded-xl border border-slate-100">
            <img src={ad.image_url} alt={ad.name} loading="lazy" decoding="async" className="w-full h-auto object-cover aspect-[16/9]"/>
          </a>
        )}
        <div className="mt-3">
          <h3 className="font-display font-extrabold text-slate-900 leading-tight text-base sm:text-lg">{ad.headline}</h3>
          {ad.subheadline && <p className="mt-1 text-sm text-slate-700 font-semibold">{ad.subheadline}</p>}
          {ad.description && <p className="mt-1 text-sm text-slate-600">{ad.description}</p>}
        </div>
        {ad.features?.length ? (
          <ul className="mt-3 flex flex-wrap gap-1.5">
            {ad.features.map((f, i) => (
              <li key={i} className="inline-flex items-center gap-1 rounded-full border border-slate-200 bg-white/80 px-2 py-0.5 text-[11px] text-slate-700">
                <span aria-hidden>{f.icon}</span> {f.label}
              </li>
            ))}
          </ul>
        ) : null}
        <a href={ad.url} target="_blank" rel="noopener noreferrer" onClick={onClick} data-testid={`promo-ad-cta-${ad.campaign_id}`}
           className={`mt-3 inline-flex w-full sm:w-auto items-center justify-center gap-2 rounded-xl ${a.btn} text-white font-bold text-sm px-4 py-2.5 shadow`}>
          🚀 {ad.cta} <ExternalLink size={14}/>
        </a>
        {ad.trust_line && <p className="mt-2 text-[11px] text-slate-500">{ad.trust_line} · <span className="text-slate-400">{new URL(ad.url).host}</span></p>}
      </aside>
    );
  }

  // -------------------------------------------------------------------------
  // Variant: horizontal — wider strip for desktop below the calculator.
  // -------------------------------------------------------------------------
  if (variant === "horizontal") {
    return (
      <aside ref={cardRef}
             role="complementary"
             aria-label={`${ad.label}: ${ad.name}`}
             className={`relative rounded-2xl border ${a.border} bg-gradient-to-r ${a.bg} p-3 sm:p-4 shadow-sm`}
             data-testid={`promo-ad-${ad.campaign_id}`}>
        <button onClick={dismiss} aria-label="Dismiss advertisement" className="absolute right-2 top-2 h-7 w-7 rounded-full hover:bg-slate-100 grid place-items-center text-slate-500" data-testid={`promo-ad-close-${ad.campaign_id}`}>
          <X size={14}/>
        </button>
        <div className="flex flex-col sm:flex-row gap-3 sm:items-center">
          {ad.image_url && (
            <a href={ad.url} target="_blank" rel="noopener noreferrer" onClick={onClick} className="block shrink-0 overflow-hidden rounded-xl border border-slate-100 sm:w-56 md:w-72">
              <img src={ad.image_url} alt={ad.name} loading="lazy" decoding="async" className="w-full h-auto object-cover aspect-[16/9]"/>
            </a>
          )}
          <div className="flex-1 min-w-0">
            <span className={`inline-flex items-center gap-1 rounded-full border ${a.chip} text-[10px] font-bold uppercase tracking-widest px-2 py-0.5`}>{ad.label}</span>
            <h3 className="mt-2 font-display font-extrabold text-slate-900 leading-tight text-base sm:text-lg">{ad.headline}</h3>
            {ad.subheadline && <p className="mt-0.5 text-sm text-slate-700 font-semibold">{ad.subheadline}</p>}
            {ad.features?.length ? (
              <ul className="mt-2 flex flex-wrap gap-1.5">
                {ad.features.slice(0, 4).map((f, i) => (
                  <li key={i} className="inline-flex items-center gap-1 rounded-full border border-slate-200 bg-white/80 px-2 py-0.5 text-[11px] text-slate-700">
                    <span aria-hidden>{f.icon}</span> {f.label}
                  </li>
                ))}
              </ul>
            ) : null}
            <a href={ad.url} target="_blank" rel="noopener noreferrer" onClick={onClick} data-testid={`promo-ad-cta-${ad.campaign_id}`}
               className={`mt-3 inline-flex items-center gap-1 rounded-xl ${a.btn} text-white font-bold text-sm px-4 py-2 shadow`}>
              {ad.cta} <ArrowRight size={14}/>
            </a>
            {ad.trust_line && <p className="mt-1 text-[11px] text-slate-500">{ad.trust_line}</p>}
          </div>
        </div>
      </aside>
    );
  }

  // -------------------------------------------------------------------------
  // Variant: modal — appears once per session, only when explicitly opened.
  // Rendered by <PromotionalAdModal /> below.
  // -------------------------------------------------------------------------
  return null;
}


// ---------------------------------------------------------------------------
// Post-report modal — trigger once per session via the imperative helper.
// ---------------------------------------------------------------------------
export function shouldShowPromotionalModal() {
  const s = safeStorage();
  if (!s) return true;
  return !s.getItem(MODAL_SHOWN_KEY);
}
export function markPromotionalModalShown() {
  const s = safeStorage(); if (!s) return;
  try { s.setItem(MODAL_SHOWN_KEY, String(Date.now())); } catch {}
}


export function PromotionalAdModal({ open, onClose, placement = "modal" }) {
  const { user } = useAuth();
  const [items, setItems] = useState([]);
  const [ad, setAd] = useState(null);
  const cardRef = useRef(null);
  const [tracked, setTracked] = useState(false);

  useEffect(() => {
    if (!open) return;
    api.adsActive(placement).then((r) => setItems(r.items || [])).catch(() => setItems([]));
  }, [open, placement]);

  useEffect(() => {
    if (!open || !items.length) return;
    setAd(pickCampaign(items, placement));
    setTracked(false);
  }, [open, items, placement]);

  useEffect(() => {
    if (!open) return;
    const onEsc = (e) => { if (e.key === "Escape") onClose && onClose(); };
    window.addEventListener("keydown", onEsc);
    return () => window.removeEventListener("keydown", onEsc);
  }, [open, onClose]);

  useEffect(() => {
    if (!open || !ad || tracked) return;
    api.adImpression(ad.campaign_id, placement, sessionId());
    markPromotionalModalShown();
    setTracked(true);
  }, [open, ad, tracked, placement]);

  const premium = !!user?.subscription?.status && user?.subscription?.status === "active" && new Date(user?.subscription?.expiry_date || 0) > new Date();
  if (!open || !ad) return null;
  if (premium && !ad.show_to_premium) return null;

  const a = ACCENT[ad.accent] || ACCENT.emerald;
  const onCta = () => {
    api.adClick(ad.campaign_id, placement, sessionId());
    onClose && onClose();
  };

  return (
    <div role="dialog" aria-modal="true" aria-label={`${ad.label}: ${ad.name}`}
         className="fixed inset-0 z-50 bg-slate-900/60 backdrop-blur-sm grid place-items-center p-4"
         onClick={onClose}
         data-testid="promo-ad-modal">
      <div ref={cardRef} onClick={(e) => e.stopPropagation()}
           className={`relative w-full max-w-md rounded-2xl border ${a.border} bg-white shadow-2xl overflow-hidden`}>
        <button onClick={onClose} aria-label="Close" data-testid="promo-ad-modal-close"
                className="absolute right-2 top-2 z-10 h-8 w-8 rounded-full bg-white/90 hover:bg-white grid place-items-center text-slate-600 shadow">
          <X size={16}/>
        </button>
        {ad.image_url && (
          <a href={ad.url} target="_blank" rel="noopener noreferrer" onClick={onCta} className="block">
            <img src={ad.image_url} alt={ad.name} loading="lazy" className="w-full h-auto object-cover aspect-[16/9]"/>
          </a>
        )}
        <div className={`p-5 bg-gradient-to-br ${a.bg}`}>
          <span className={`inline-flex items-center gap-1 rounded-full border ${a.chip} text-[10px] font-bold uppercase tracking-widest px-2 py-0.5`}>{ad.label} · Recommended for Teachers</span>
          <h3 className="mt-2 font-display font-extrabold text-slate-900 leading-tight text-lg sm:text-xl">{ad.headline}</h3>
          {ad.subheadline && <p className="mt-1 text-sm text-slate-700 font-semibold">{ad.subheadline}</p>}
          {ad.description && <p className="mt-1 text-sm text-slate-600">{ad.description}</p>}
          {ad.features?.length ? (
            <ul className="mt-3 grid grid-cols-2 gap-1.5">
              {ad.features.map((f, i) => (
                <li key={i} className="inline-flex items-center gap-1 rounded-full border border-slate-200 bg-white/80 px-2 py-0.5 text-[11px] text-slate-700">
                  <span aria-hidden>{f.icon}</span> {f.label}
                </li>
              ))}
            </ul>
          ) : null}
          <a href={ad.url} target="_blank" rel="noopener noreferrer" onClick={onCta} data-testid={`promo-ad-modal-cta-${ad.campaign_id}`}
             className={`mt-4 inline-flex w-full items-center justify-center gap-2 rounded-xl ${a.btn} text-white font-bold px-4 py-3 shadow`}>
            🚀 {ad.cta} <ExternalLink size={14}/>
          </a>
          <button onClick={onClose} className="mt-2 w-full text-center text-xs text-slate-500 hover:text-slate-800 py-1" data-testid="promo-ad-modal-later">Maybe later</button>
          {ad.trust_line && <p className="mt-2 text-center text-[11px] text-slate-500">{ad.trust_line}</p>}
        </div>
      </div>
    </div>
  );
}
